import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useLocation } from 'react-router-dom'
import useSWR from 'swr'
import { useAuth } from '../auth/AuthContext'
import { apiGet } from '../config/api'
import type { NotificationsListResponse } from '../types/notification'
import {
  type AppNavItem,
  type AppTopNavEntry,
  type NavTone,
  appNavGroups,
  appNavHomeItem,
  buildAppTopNavEntries,
  filterNavGroups,
  filterNavItems,
  isTopNavMenuActive,
} from './appNavConfig'

const toneActiveTop: Record<NavTone, string> = {
  violet: 'text-violet-700 after:bg-violet-600 dark:text-violet-300',
  emerald: 'text-emerald-700 after:bg-emerald-600 dark:text-emerald-300',
  amber: 'text-amber-800 after:bg-amber-500 dark:text-amber-300',
  sky: 'text-sky-700 after:bg-sky-600 dark:text-sky-300',
  zinc: 'text-zinc-900 after:bg-zinc-700 dark:text-zinc-200',
  bronze: 'text-orange-800 after:bg-orange-600 dark:text-orange-300',
  silver: 'text-slate-700 after:bg-slate-500 dark:text-slate-300',
  gold: 'text-yellow-800 after:bg-yellow-600 dark:text-yellow-300',
}

function isItemActive(item: AppNavItem, pathname: string): boolean {
  if (item.isActive) return item.isActive(pathname)
  if (item.end) return pathname === item.to
  return pathname === item.to || pathname.startsWith(`${item.to}/`)
}

function TopNavLink({
  item,
  pathname,
  badge,
}: {
  item: AppNavItem
  pathname: string
  badge?: number
}) {
  const active = isItemActive(item, pathname)
  const { Icon } = item

  return (
    <NavLink
      to={item.to}
      end={item.end}
      aria-current={active ? 'page' : undefined}
      className={`relative flex shrink-0 items-center gap-2 whitespace-nowrap px-3.5 py-3 text-sm font-semibold transition after:absolute after:inset-x-3.5 after:bottom-0 after:h-0.5 after:rounded-full after:transition ${
        active
          ? `${toneActiveTop[item.tone]} after:opacity-100`
          : 'text-zinc-500 after:opacity-0 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200'
      }`}
    >
      <Icon className="hidden h-4 w-4 sm:block" />
      <span>{item.label}</span>
      {badge && badge > 0 ? (
        <span className="inline-flex min-w-[1.15rem] justify-center rounded-full bg-violet-600 px-1.5 text-[10px] font-bold leading-[1.125rem] text-white dark:bg-violet-500">
          {badge > 99 ? '99+' : badge}
        </span>
      ) : null}
    </NavLink>
  )
}

function TopNavDropdown({
  label,
  tone,
  items,
  pathname,
  unreadCount,
}: {
  label: string
  tone: NavTone
  items: AppNavItem[]
  pathname: string
  unreadCount: number
}) {
  const panelId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [panelPos, setPanelPos] = useState<{ top: number; left: number } | null>(
    null,
  )
  const active = isTopNavMenuActive(items, pathname)
  const showUnread = items.some((i) => i.to === '/notifications') && unreadCount > 0

  const close = useCallback(() => {
    setOpen(false)
    setPanelPos(null)
  }, [])

  const measurePanelPos = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return null
    const triggerRect = trigger.getBoundingClientRect()
    const navRect = trigger.closest('nav')?.getBoundingClientRect()
    return {
      top: navRect?.bottom ?? triggerRect.bottom,
      left: triggerRect.left,
    }
  }, [])

  const openMenu = useCallback(() => {
    const pos = measurePanelPos()
    if (!pos) return
    setPanelPos(pos)
    setOpen(true)
  }, [measurePanelPos])

  const toggleMenu = useCallback(() => {
    if (open) close()
    else openMenu()
  }, [close, open, openMenu])

  useEffect(() => {
    close()
  }, [pathname, close])

  useLayoutEffect(() => {
    if (!open) return
    setPanelPos(measurePanelPos())
  }, [open, measurePanelPos])

  useEffect(() => {
    if (!open) return
    const onLayout = () => setPanelPos(measurePanelPos())
    window.addEventListener('resize', onLayout)
    window.addEventListener('scroll', onLayout, true)
    return () => {
      window.removeEventListener('resize', onLayout)
      window.removeEventListener('scroll', onLayout, true)
    }
  }, [open, measurePanelPos])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      close()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [close, open, panelId])

  const panel =
    open && panelPos
      ? createPortal(
          <div
            ref={panelRef}
            id={panelId}
            className="fixed z-[200] min-w-[12rem] overflow-hidden rounded-xl border border-zinc-200/90 bg-white py-1 shadow-xl ring-1 ring-black/5 dark:border-zinc-600 dark:bg-zinc-900 dark:ring-white/10"
            style={{ top: panelPos.top, left: panelPos.left }}
            role="menu"
          >
            {items.map((item) => {
              const itemActive = isItemActive(item, pathname)
              const badge = item.to === '/notifications' ? unreadCount : 0
              const { Icon } = item
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  role="menuitem"
                  aria-current={itemActive ? 'page' : undefined}
                  className={`flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium transition ${
                    itemActive
                      ? 'bg-violet-50 text-violet-800 dark:bg-violet-950/50 dark:text-violet-200'
                      : 'text-zinc-700 hover:bg-zinc-50 dark:text-zinc-200 dark:hover:bg-zinc-800/80'
                  }`}
                  onClick={close}
                >
                  <Icon className="h-4 w-4 shrink-0 opacity-70" />
                  <span className="flex-1">{item.label}</span>
                  {badge > 0 ? (
                    <span className="inline-flex min-w-[1.15rem] justify-center rounded-full bg-violet-600 px-1.5 text-[10px] font-bold leading-5 text-white">
                      {badge > 99 ? '99+' : badge}
                    </span>
                  ) : null}
                </NavLink>
              )
            })}
          </div>,
          document.body,
        )
      : null

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={open ? panelId : undefined}
        onClick={toggleMenu}
        className={`relative flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap px-3.5 py-3 text-sm font-semibold transition after:absolute after:inset-x-3.5 after:bottom-0 after:h-0.5 after:rounded-full after:transition ${
          active
            ? `${toneActiveTop[tone]} after:opacity-100`
            : 'text-zinc-500 after:opacity-0 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200'
        }`}
      >
        <span>{label}</span>
        {showUnread ? (
          <span className="inline-flex min-w-[1.15rem] justify-center rounded-full bg-violet-600 px-1.5 text-[10px] font-bold leading-[1.125rem] text-white dark:bg-violet-500">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        ) : null}
        <svg
          className={`h-4 w-4 shrink-0 opacity-60 transition ${open ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.24 4.5a.75.75 0 01-1.08 0l-4.24-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {panel}
    </>
  )
}

function TopNavEntry({
  entry,
  pathname,
  unreadCount,
}: {
  entry: AppTopNavEntry
  pathname: string
  unreadCount: number
}) {
  if (entry.type === 'link') {
    return <TopNavLink item={entry.item} pathname={pathname} />
  }
  return (
    <TopNavDropdown
      label={entry.label}
      tone={entry.tone}
      items={entry.items}
      pathname={pathname}
      unreadCount={unreadCount}
    />
  )
}

export function AppTopNavMenu() {
  const { user } = useAuth()
  const { pathname } = useLocation()

  const { data: notifData } = useSWR(
    (user?.entitlements ?? []).includes('notifications') ? 'notifications' : null,
    () => apiGet<NotificationsListResponse>('/notifications'),
  )

  const entitlements = user?.entitlements ?? []
  const topItems = filterNavItems([appNavHomeItem], entitlements)
  const groups = filterNavGroups(appNavGroups, entitlements)
  const entries = buildAppTopNavEntries(topItems[0] ?? appNavHomeItem, groups)
  const unreadCount = notifData?.unreadCount ?? 0

  return (
    <>
      {entries.map((entry) => (
        <TopNavEntry
          key={entry.type === 'link' ? entry.item.to : entry.id}
          entry={entry}
          pathname={pathname}
          unreadCount={unreadCount}
        />
      ))}
    </>
  )
}
